/**
 * Sistema de logging para o DelivAI
 *
 * Em produção (NODE_ENV=production): só WARN e ERROR vão ao console.
 * logDebug / logger.debug não imprimem — evita vazar payloads de API, cardápio e clientes.
 *
 * Em desenvolvimento: DEBUG ativo (todos os logDebug aparecem).
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

class Logger {
  private level: LogLevel;

  constructor() {
    // Em produção, apenas erros e warnings
    // Em desenvolvimento, todos os logs
    this.level = process.env.NODE_ENV === 'production' 
      ? LogLevel.WARN 
      : LogLevel.DEBUG;
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.level;
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(`[ERROR] ${message}`, ...args);
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  info(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  debug(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  }

  // Método para logs de desenvolvimento (sempre exibidos)
  dev(message: string, ...args: any[]): void {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[DEV] ${message}`, ...args);
    }
  }
}

// Instância singleton do logger
export const logger = new Logger();

// Funções de conveniência
export const logError = (message: string, ...args: any[]) => logger.error(message, ...args);
export const logWarn = (message: string, ...args: any[]) => logger.warn(message, ...args);
export const logInfo = (message: string, ...args: any[]) => logger.info(message, ...args);
export const logDebug = (message: string, ...args: any[]) => logger.debug(message, ...args);
export const logDev = (message: string, ...args: any[]) => logger.dev(message, ...args);
